import React from 'react';
import { Badge } from '../../ui/badge';
import { ProcessingStatus } from '../../../types/signals-live';
import { processingStatusLabel } from '../../../utils/processingStatus';

export interface SignalProcessingStatusBadgeProps {
  /** Current processing status from signal.processing_status */
  processingStatus?: ProcessingStatus | string | null;
  /** Signal status (e.g., 'draft') */
  status?: string | null;
  /** Real-time progress stage from WebSocket updates (e.g., 'starting', 'analyzing', 'classifying') */
  progressStage?: string | null;
  /** Progress percentage (0-100) */
  progressPercent?: number | null;
  /** Custom className for the badge */
  className?: string;
}

/**
 * Reusable component for displaying signal processing status badges
 * Handles both static processing_status and real-time progress stages
 */
const SignalProcessingStatusBadge: React.FC<SignalProcessingStatusBadgeProps> = ({
  processingStatus,
  status,
  progressStage,
  progressPercent,
  className = ''
}) => {
  // Check if signal is in draft status
  if (status === 'draft') {
    return (
      <Badge variant="outline" className={`text-xs ${className}`}>
        Draft
      </Badge>
    );
  }

  // Use real-time progress stage if available (more granular than processing_status)
  const effectiveStage = progressStage || processingStatus;

  // If no status is set yet, assume it's queued
  if (!effectiveStage) {
    return (
      <Badge variant="secondary" className={`text-xs ${className}`}>
        Queued
      </Badge>
    );
  }

  const stage = String(effectiveStage).toLowerCase();

  // Real-time processing stages (from WebSocket progress updates)
  if (stage === 'starting') {
    return (
      <Badge variant="secondary" className={`text-xs ${className}`}>
        Starting
      </Badge>
    );
  }

  if (stage === 'analyzing') {
    return (
      <Badge variant="secondary" className={`text-xs ${className}`}>
        Analyzing
      </Badge>
    );
  }

  // Standard processing stages
  if (stage === 'queued' || stage === 'pending') {
    return (
      <Badge variant="secondary" className={`text-xs ${className}`}>
        Queued
      </Badge>
    );
  }

  // Redaction could not run, so the signal never reached the model. The sweeper
  // returns it to the queue once the cause is fixed, so to a reader it is
  // genuinely still queued — not an error state, and not something to act on.
  if (stage === 'redaction_blocked') {
    return (
      <Badge variant="secondary" className={`text-xs ${className}`}>
        Queued
      </Badge>
    );
  }

  if (stage === 'classifying') {
    return (
      <Badge variant="secondary" className={`text-xs ${className}`}>
        Classifying
      </Badge>
    );
  }

  if (stage === 'summarising' || stage === 'summarizing') {
    return (
      <Badge variant="secondary" className={`text-xs ${className}`}>
        Summarising
      </Badge>
    );
  }

  if (stage === 'evaluating_ombudsman' || stage === 'evaluating ombudsman') {
    return (
      <Badge variant="secondary" className={`text-xs ${className}`}>
        Evaluating FOS
      </Badge>
    );
  }

  // Error states
  if (stage === 'failed') {
    return (
      <Badge variant="destructive" className={`text-xs ${className}`}>
        Failed
      </Badge>
    );
  }

  // Completion states
  if (stage === 'processed') {
    return (
      <Badge variant="outline" className={`text-xs ${className}`}>
        Processed
      </Badge>
    );
  }

  if (stage === 'classified' || stage === 'done') {
    return (
      <Badge variant="outline" className={`text-xs ${className}`}>
        Classified
      </Badge>
    );
  }

  // Fallback: the shared mapping owns how a stored status reads, so a state
  // added there renders correctly here without needing a branch of its own.
  // Unknown values (live progress stages) still title-case.
  const displayLabel = processingStatusLabel(stage);

  return (
    <Badge variant="secondary" className={`text-xs ${className}`}>
      {displayLabel}
    </Badge>
  );
};

export default SignalProcessingStatusBadge;

